// Components==============
import {
  faCheckCircle,
  faExchange,
  faEye,
  faGlobe,
  faPlus,
  faTrash,
  faUsers,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AddActivityModal from 'components/UI/AddActivityModal/AddActivityModal';
import CircleButton from 'components/UI/CircleButton/CircleButton';
import { DreamModalContext } from 'components/UI/DreamModal/DreamModal';
import ProgressManager from 'components/UI/DreamModal/ProgressManager/ProgressManager';
import Label from 'components/UI/Label/Label';
import Tooltip from 'components/UI/Tooltip/Tooltip';
import { AnimatePresence, motion } from 'framer-motion';
import { usePermission } from 'hooks/usePermission';
import { useRedux } from 'hooks/useRedux';
import { useRootModal } from 'hooks/useRootModal';
import { useTranslate } from 'hooks/useTranslate';
import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router';
import { framerMountAndScale } from 'utilities/FramerAnimations';
import { generateTrainingUrl } from 'utilities/generateTrainingUrl';
import { getSmartImage } from 'utilities/getSmartImage';
import { TrainingCardContext } from '../../TrainingCard';
import TrainingModal from '../TrainingModal/TrainingModal';
import './Card.module.scss';
// =========================

const framerOverlay = {
  initial: {
    opacity: 0.8,
  },
  whileHover: {
    opacity: 0,
  },
};

const framerChild = {
  initial: {
    opacity: 1,
  },
  whileHover: {
    opacity: 0,
  },
};

export default function Card() {
  const {
    training,
    categories,
    withModal,
    small,
    // skills,
    handleAdd,
    handleDelete,
    handleSwitch,
    canSwitch,
    process,
    isUsed,
    height,
    inLibrary,
  } = useContext(TrainingCardContext);

  const { dream } = useContext(DreamModalContext);
  const inDreamModal = !!dream;

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const isEditable =
    !!handleSwitch || !!handleAdd || !!handleDelete || canSwitch;

  const translate = useTranslate();

  const { setModal } = useRootModal();
  const history = useHistory();

  function handleClick(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    if (withModal)
      return setModal(
        <TrainingModal
          training={training}
          categories={categories}
          // skills={skills}
        />
      );

    const url = training ? generateTrainingUrl(training) : '';

    history.push(url);
  }

  const date = training?.created_at
    ? new Date(training?.created_at?.split(' ')[0]).toLocaleDateString('nl')
    : null;

  const firstImage = training?.attachments
    ?.filter((a) => a.attachment_type === 'image')
    .map((a) => JSON.parse(a.attachment_data).file_path)[0] as
    | string
    | undefined;

  const lessonCount = training?.sessions?.reduce(
    (count, session) =>
      session.lessons ? count + session?.lessons?.length : 0,
    0
  );

  const clonedTraining = useRedux((state) =>
    training?.cloned_from_id
      ? state.training?.entities.byId[training?.cloned_from_id]
      : undefined
  );

  const isStudent = usePermission(['Leerling', 'Leerling Portfolio']);
  const teacher = `${training?.owner?.first_name} ${training?.owner?.last_name}`;

  const userId = useRedux((state) => state.authentication?.user?.id);

  const subscriber = useRedux((state) =>
    state.trainingSubscription?.entities.allIds
      .map((id) => state.trainingSubscription?.entities.byId[id])
      .find((s) => s?.user_id === userId && s?.training_id === training?.id)
  );

  return (
    <>
      <div
        styleName={`container ${isEditable ? 'permit-hover' : ''} ${
          isUsed ? 'is-used' : ''
        }`}
      >
        {isUsed && (
          <div styleName="is-used">
            {translate('project.already-in-use.training')}
          </div>
        )}
        <motion.div
          initial="initial"
          whileHover="whileHover"
          whileTap="whileTap"
          animate="animate"
          styleName={`wrapper ${small ? 'small' : ''}`}
          variants={framerMountAndScale}
          onClick={handleClick}
          style={{ height }}
        >
          <div styleName="wrapper-content">
            {!!firstImage && (
              <img src={getSmartImage(firstImage)} alt="" width="100" />
            )}
            <motion.div variants={framerOverlay} styleName="overlay" />
            <div styleName="top">
              <h2>
                {!!(!!training?.completed && !inLibrary) && (
                  <FontAwesomeIcon icon={faCheckCircle} />
                )}{' '}
                {training?.title}
              </h2>
              <motion.div variants={framerChild} styleName="labels">
                {training?.categories?.map((cat, i) => {
                  if (small ? i > 2 : i > 3) return;

                  return (
                    <Label key={cat.category_id} type="white">
                      {categories?.find((c) => c?.id === cat.category_id)?.name}
                    </Label>
                  );
                })}
                {training?.categories && training?.categories?.length > 4 && (
                  <Label type="white">
                    {`+ ${training?.categories.length - 4}`}
                  </Label>
                )}
              </motion.div>
            </div>
            <div styleName="bottom">
              {!small && training?.visibility !== 'private' && (
                <div styleName="icon-wrap">
                  <Tooltip
                    noPointer
                    content={
                      training?.visibility === 'public'
                        ? translate('course.visibility.public.tooltip')
                        : translate('course.visibility.organization.tooltip')
                    }
                  >
                    <div styleName="icon">
                      <FontAwesomeIcon
                        icon={
                          training?.visibility === 'public' ? faGlobe : faUsers
                        }
                      />
                    </div>
                  </Tooltip>
                </div>
              )}
              <motion.div variants={framerChild} styleName="info">
                {!small && (
                  <div styleName="row">
                    <p>
                      {training?.cloned_from_id
                        ? `${clonedTraining?.owner?.first_name} ${clonedTraining?.owner?.last_name}`
                        : `${training?.owner?.first_name} ${training?.owner?.last_name}`}
                    </p>
                    <p>{date}</p>
                  </div>
                )}
                {!!(training?.sessions?.length || lessonCount) && (
                  <div styleName="lesson-session row">
                    {training?.sessions?.length ? (
                      <p>
                        {`${training?.sessions?.length} ${
                          training?.sessions?.length > 1
                            ? translate('course.terms.sessions')
                            : translate('course.terms.session')
                        }`}
                      </p>
                    ) : null}
                    {lessonCount ? (
                      <p>
                        {`${lessonCount} ${
                          lessonCount > 1
                            ? translate('course.terms.lessons')
                            : translate('course.terms.lesson')
                        }`}
                      </p>
                    ) : null}
                  </div>
                )}
                {!withModal && (
                  <>
                    {isStudent ? (
                      <>
                        {!!(teacher && subscriber?.used_sharecode) && (
                          <p styleName="extra-info">
                            <FontAwesomeIcon icon={faEye} /> {teacher}
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        {!!(
                          training?.subscriptions_count &&
                          training.subscriptions_count >= 1
                        ) && (
                          <p styleName="extra-info">
                            {training?.subscriptions_count}{' '}
                            {training.subscriptions_count > 1
                              ? translate('general.terms.students')
                              : translate('general.terms.student')}
                          </p>
                        )}
                      </>
                    )}
                  </>
                )}
              </motion.div>
            </div>
          </div>
          <ProgressManager trainingIsFinished={!!training?.completed} />
          {isEditable && (
            <div styleName="overlay-wrapper">
              {!!handleDelete && (
                <div styleName="delete">
                  <CircleButton
                    color={inDreamModal ? 'white-blue-shadow' : 'white-blue'}
                    icon={faTrash}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete();
                    }}
                  />
                </div>
              )}
              {!!handleAdd && (
                <div styleName="add-switch">
                  <CircleButton
                    color="white-blue"
                    icon={faPlus}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAdd();
                    }}
                  />
                </div>
              )}
              {!!handleSwitch && (
                <div
                  styleName="add-switch"
                  style={{ transform: 'rotate(-45deg)' }}
                >
                  <CircleButton
                    color="white-blue"
                    icon={faExchange}
                    onClick={(e) => {
                      e.stopPropagation();
                      translate('project.confirm.switch-activity');
                    }}
                  />
                </div>
              )}
              {canSwitch && (
                <div
                  styleName="add-switch"
                  style={{ transform: 'rotate(-45deg)' }}
                >
                  <CircleButton
                    color="white-blue"
                    icon={faExchange}
                    onClick={(e) => {
                      e.stopPropagation();
                      setModalIsOpen(true);
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>

      <AnimatePresence>
        {modalIsOpen && (
          <AddActivityModal
            permission={process?.process_type}
            fn="switch"
            origin="project"
            setModalIsOpen={setModalIsOpen}
          />
        )}
      </AnimatePresence>
    </>
  );
}
